import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePopularTerms } from '../../hooks/usePopularTerms';
import { useSearchSuggestions } from '../../hooks/useSearchSuggestions';
import { useAllInterests } from '../../hooks/useAllInterests';
import { useAllProfessions } from '../../hooks/useAllProfessions';
import { HorizontalCardSlider } from './HorizontalCardSlider';
import { FramedAvatar } from '../ui/FramedAvatar';
import { Plus, RefreshCw, Search } from 'lucide-react';
import BeltIcon from '@/assets/images/icons/belt.png';
import { useI18n } from '@/contexts/I18nContext';

interface SearchSuggestionsProps {
  onSearch?: (query: string) => void;
  newSearchTerm?: string; // Nova prop para receber novos termos
}

export const SearchSuggestions: React.FC<SearchSuggestionsProps> = ({ onSearch, newSearchTerm }) => {
  const navigate = useNavigate();
  const { t } = useI18n();
  
  // Hooks customizados primeiro
  const { popularTerms, isLoading: isLoadingTerms, addTermLocally } = usePopularTerms();
  const { suggestions, isLoading: isLoadingSuggestions, handleConnect, isConnecting } = useSearchSuggestions();
  const { allInterests, isLoading: isLoadingInterests, refetch: refetchInterests } = useAllInterests();
  const { allProfessions, isLoading: isLoadingPopularProfessions } = useAllProfessions();

  // Estado local
  const [localPopularTerms, setLocalPopularTerms] = React.useState<string[]>(popularTerms || []);

  // Funções
  const handleTermClick = (term: string) => {
    onSearch?.(term);
  };

  const handleRefreshInterests = () => {
    refetchInterests();
  };

  const handleUserCardClick = (username: string) => {
    navigate(`/profile/${username}`);
  };

  const handleConnectClick = (event: React.MouseEvent, userId: string) => {
    event.stopPropagation(); // Previne que o clique se propague para o card
    handleConnect(userId);
  };

  // UseEffects
  React.useEffect(() => {
    if (popularTerms && popularTerms.length > 0) {
      setLocalPopularTerms(popularTerms);
    }
  }, [popularTerms]);

  React.useEffect(() => {
    if (newSearchTerm && newSearchTerm.trim()) {
      setLocalPopularTerms(prevTerms => {
        // Se o termo já existe, movê-lo para o início
        const filtered = prevTerms.filter(term => term !== newSearchTerm.trim());
        const newTerms = [newSearchTerm.trim(), ...filtered].slice(0, 10); // Manter apenas os 10 primeiros
        return newTerms;
      });
    }
  }, [newSearchTerm]);

  // Computed values
  const displayTerms = localPopularTerms.length > 0 ? localPopularTerms : popularTerms;

  // Função para localizar nomes de interesses
  const getLocalizedInterestName = (interestName: string): string => {
    // Mapeamento de valores do banco para chaves de tradução
    const interestMapping: Record<string, string> = {
      'Reading': 'reading',
      'Hiking / Trekking': 'hiking_trekking',
      'Photography': 'photography',
      'Gaming (Video Games / Board Games)': 'gaming',
      'Cooking / Baking': 'cooking_baking',
      'Traveling': 'traveling',
      'Cycling (Road / Mountain)': 'cycling',
      'Running': 'running',
      'Weightlifting / Fitness': 'weightlifting_fitness',
      'Yoga / Pilates': 'yoga_pilates',
      'Swimming': 'swimming',
      'Surfing / Paddleboarding': 'surfing_paddleboarding',
      'Fishing': 'fishing',
      'Gardening': 'gardening',
      'Music (Playing an instrument / Listening)': 'music',
      'Podcasts': 'podcasts',
      'Movies / TV Shows': 'movies_tv_shows',
      'Drawing / Painting': 'drawing_painting',
      'Writing': 'writing',
      'Learning Languages': 'learning_languages',
      'Volunteering': 'volunteering',
      'Meditation': 'meditation',
      'Camping': 'camping',
      'Motorcycling': 'motorcycling',
      'Cars / Motorsports': 'cars_motorsports',
      'Collecting (e.g., cards, coins, stamps)': 'collecting',
      'DIY / Home Improvement': 'diy_home_improvement',
      'Chess / Strategy Games': 'chess_strategy_games',
      'Martial Arts (other than Jiu-Jitsu)': 'martial_arts',
      'Team Sports (e.g., Soccer, Basketball, Volleyball)': 'team_sports',
      'Golf': 'golf',
      'Tennis': 'tennis',
      'Skiing / Snowboarding': 'skiing_snowboarding',
      'Table Tennis / Ping Pong': 'table_tennis',
      'Bowling': 'bowling',
      'Dancing': 'dancing',
      'Fashion / Style': 'fashion_style',
      'Astrology / Spirituality': 'astrology_spirituality',
      'Podcasting / Content Creation': 'podcasting_content_creation',
      'Woodworking': 'woodworking',
      'Knitting / Crocheting': 'knitting_crocheting',
      'Calligraphy': 'calligraphy',
      'Coding / Programming (as a hobby)': 'coding_programming',
      'Brewing (Beer / Coffee)': 'brewing',
      'Astronomy': 'astronomy',
      'Birdwatching': 'birdwatching',
      'Archery': 'archery',
      'Hunting': 'hunting',
      'Puzzles': 'puzzles',
      'Other': 'other'
    };

    const key = interestMapping[interestName];
    if (key) {
      return t(`search.popular_interests.${key}`);
    }
    
    // Se não encontrar mapeamento, retorna o nome original
    return interestName;
  };

  // Função para localizar nomes de profissões
  const getLocalizedProfessionName = (professionName: string): string => {
    // Mapeamento de valores do banco para chaves de tradução
    const professionMapping: Record<string, string> = {
      'Accountant': 'accountant',
      'Acupuncturist': 'acupuncturist',
      'Administrator': 'administrator',
      'Animator': 'animator',
      'Artist': 'artist',
      'Barber / Hair Stylist': 'barber_hair_stylist',
      'Business Analyst': 'business_analyst',
      'Carpenter': 'carpenter',
      'Chef / Cook': 'chef_cook',
      'Chiropractor': 'chiropractor',
      'Cloud Engineer': 'cloud_engineer',
      'Coach': 'coach',
      'Consultant': 'consultant',
      'Content Creator': 'content_creator',
      'Customer Service Representative': 'customer_service_representative',
      'Cybersecurity Analyst': 'cybersecurity_analyst',
      'Data Scientist': 'data_scientist',
      'Dentist': 'dentist',
      'DevOps Engineer': 'devops_engineer',
      'Digital Marketing Specialist': 'digital_marketing_specialist',
      'Doctor': 'doctor',
      'Electrician': 'electrician',
      'Entrepreneur / Business Owner': 'entrepreneur_business_owner',
      'Financial Advisor': 'financial_advisor',
      'Financial Analyst': 'financial_analyst',
      'Firefighter': 'firefighter',
      'Graphic Designer': 'graphic_designer',
      'Human Resources Manager': 'human_resources_manager',
      'Investment Banker': 'investment_banker',
      'IT Consultant': 'it_consultant',
      'IT Support Specialist': 'it_support_specialist',
      'Jiu-Jitsu Academy Owner': 'jiu_jitsu_academy_owner',
      'Jiu-Jitsu Instructor / Professor': 'jiu_jitsu_instructor_professor',
      'Journalist': 'journalist',
      'Judge': 'judge',
      'Law Enforcement Officer': 'law_enforcement_officer',
      'Lawyer / Attorney': 'lawyer_attorney',
      'Librarian': 'librarian',
      'Logistics Coordinator': 'logistics_coordinator',
      'Marketing Specialist': 'marketing_specialist',
      'Massage Therapist': 'massage_therapist',
      'Mechanic': 'mechanic',
      'Medical Assistant': 'medical_assistant',
      'Military Personnel': 'military_personnel',
      'Mobile App Developer': 'mobile_app_developer',
      'Musician': 'musician',
      'Network Administrator': 'network_administrator',
      'Nurse': 'nurse',
      'Nutritionist / Dietitian': 'nutritionist_dietitian',
      'Operations Manager': 'operations_manager',
      'Other': 'other',
      'Paralegal': 'paralegal',
      'Personal Trainer': 'personal_trainer',
      'Pharmacist': 'pharmacist',
      'Photographer': 'photographer',
      'Physical Therapist': 'physical_therapist',
      'Plumber': 'plumber',
      'Product Manager': 'product_manager',
      'Professional Jiu-Jitsu Athlete': 'professional_jiu_jitsu_athlete',
      'Professor': 'professor',
      'Project Manager': 'project_manager',
      'Public Relations Specialist': 'public_relations_specialist',
      'Real Estate Agent': 'real_estate_agent',
      'Researcher': 'researcher',
      'Restaurant Manager': 'restaurant_manager',
      'Retail Associate': 'retail_associate',
      'Retired': 'retired',
      'Sales Professional': 'sales_professional',
      'Social Media Manager': 'social_media_manager',
      'Social Worker': 'social_worker',
      'Software Developer': 'software_developer',
      'Sports Psychologist': 'sports_psychologist',
      'Student': 'student',
      'Systems Analyst': 'systems_analyst',
      'Teacher / Educator': 'teacher_educator',
      'Unemployed / Seeking Opportunities': 'unemployed_seeking_opportunities',
      'UX/UI Designer': 'ux_ui_designer',
      'Videographer': 'videographer',
      'Web Developer': 'web_developer',
      'Writer / Editor': 'writer_editor'
    };

    const key = professionMapping[professionName];
    if (key) {
      return t(`search.popular_professions.${key}`);
    }
    
    // Se não encontrar mapeamento, retorna o nome original
    return professionName;
  };

  const interestSliderItems = allInterests?.map((interest) => ({
    label: getLocalizedInterestName(interest),
    onSeeAll: () => onSearch?.(interest),
  })) || [];

  const professionSliderItems = allProfessions?.map((profession) => ({
    label: getLocalizedProfessionName(profession),
    onSeeAll: () => onSearch?.(profession),
  })) || [];

  return (
    <div className="space-y-8">
      {/* Most Searched Terms */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-roboto font-extrabold text-[12px] text-white">{t('calendar.most_searched_terms')}</h2>
        </div>
        {isLoadingTerms ? (
          <div className="flex space-x-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-6 bg-white/10 rounded-full animate-pulse w-20"></div>
            ))}
          </div>
        ) : displayTerms.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {displayTerms.map((term) => (
              <button
                key={term}
                onClick={() => handleTermClick(term)}
                className="px-3 py-[1px] bg-transparent border border-white rounded-full text-white hover:bg-white/10 hover:border-[#D72E16] transition-all duration-200 pill-poppins text-[10.5px]"
              >
                {term}
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-white/40" />
            </div>
            <h3 className="font-roboto font-medium text-white text-sm mb-2">Start searching to save your terms</h3>
            <p className="text-white/60 text-xs font-roboto max-w-xs mx-auto">
              Your search history will appear here, making it easy to find what you've looked for before.
            </p>
          </div>
        )}
      </section>

      {/* Linha divisória com faixa */}
      <div className="flex items-center my-6">
        <div className="flex-1 h-px bg-white/20" />
        <img src={BeltIcon} alt="Belt" className="mx-4 w-8 h-4 object-contain" />
        <div className="flex-1 h-px bg-white/20" />
      </div>

      {/* Suggestions for you */}
      <section>
        <h2 className="font-roboto font-extrabold text-[12px] mb-4 text-white">{t('calendar.suggestions_for_you')}</h2>
        <div className="space-y-4">
          {isLoadingSuggestions ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center space-x-4 p-4 bg-transparent rounded-lg animate-pulse">
                  <div className="w-12 h-12 bg-white/10 rounded-full"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-white/10 rounded w-32"></div>
                    <div className="h-3 bg-white/10 rounded w-24"></div>
                  </div>
                  <div className="w-20 h-8 bg-white/10 rounded-full"></div>
                </div>
              ))}
            </div>
          ) : (
            suggestions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mb-4">
                  <Plus className="w-8 h-8 text-white/40" />
                </div>
                <h3 className="font-roboto font-medium text-white text-sm mb-2">No suggestions available</h3>
                <p className="text-white/60 text-xs font-roboto max-w-xs">
                  You've already connected with all suggested users! 
                  Try searching for new community members.
                </p>
              </div>
            ) : (
              suggestions.map((suggestion, idx) => (
                <div 
                  key={suggestion.id} 
                  onClick={() => handleUserCardClick(suggestion.username)}
                  className="flex items-center justify-between py-2 bg-transparent rounded-lg hover:bg-white/10 transition-colors duration-200 cursor-pointer"
                >
                  <div className="flex items-center space-x-4">
                    <FramedAvatar
                      beltRank={suggestion.belt_rank || ['blue', 'purple', 'brown'][idx % 3]}
                      initials={suggestion.full_name.split(' ').map(n => n[0]).join('').slice(0,2)}
                      alt={suggestion.full_name}
                      frameSize={{ w: 38, h: 40 }}
                    />
                    <div>
                      <h3 className="font-medium text-white font-roboto text-sm">{suggestion.full_name}</h3>
                      <p className="text-white/60 text-xs font-roboto">@{suggestion.username}</p>
                      {suggestion.academy_name && (
                        <p className="text-white/40 text-xs font-roboto">{suggestion.academy_name}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(event) => handleConnectClick(event, suggestion.id)}
                    disabled={isConnecting(suggestion.id)}
                    className="flex items-center gap-1 px-4 py-1 border border-white text-white rounded-full font-roboto font-bold text-[14px] hover:bg-white/10 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-4 h-4" /> 
                    {isConnecting(suggestion.id) ? t('calendar.sending') : t('calendar.connect')}
                  </button>
                </div>
              ))
            )
          )}
        </div>
      </section>

      {/* Linha divisória com faixa */}
      <div className="flex items-center my-6">
        <div className="flex-1 h-px bg-white/20" />
        <img src={BeltIcon} alt="Belt" className="mx-4 w-8 h-4 object-contain" />
        <div className="flex-1 h-px bg-white/20" />
      </div>

      {/* Professions - slider com profissões populares */}
      <section>
        <h2 className="font-roboto font-extrabold text-[12px] mb-4 text-white">{t('calendar.looking_for_someone')}</h2>
        {isLoadingPopularProfessions ? (
          <div className="overflow-x-auto pb-2 scrollbar-hide">
            <div className="flex space-x-4 min-w-full">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="flex flex-col items-center justify-center min-w-[180px] w-44 h-32 bg-white/5 rounded-2xl px-4 py-4 mr-2 last:mr-0 animate-pulse"
                >
                  <div className="w-24 h-6 bg-white/10 rounded-full mb-4"></div>
                  <div className="w-20 h-8 bg-white/10 rounded-full"></div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {professionSliderItems.length > 0 ? (
              <HorizontalCardSlider items={professionSliderItems} />
            ) : (
              <div className="text-white/60 text-xs font-roboto">{t('search.no_professions_found')}</div>
            )}
          </>
        )}
      </section>

      {/* Linha divisória com faixa */}
      <div className="flex items-center my-6">
        <div className="flex-1 h-px bg-white/20" />
        <img src={BeltIcon} alt="Belt" className="mx-4 w-8 h-4 object-contain" />
        <div className="flex-1 h-px bg-white/20" />
      </div>

      {/* Interests - agora slider */}
      <section>
        <div className="mb-4">
          <h2 className="font-roboto font-extrabold text-[12px] text-white">{t('calendar.find_your_tribe')}</h2>
        </div>
        {isLoadingInterests ? (
          <div className="overflow-x-auto pb-2 scrollbar-hide">
            <div className="flex space-x-4 min-w-full">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="flex flex-col items-center justify-center min-w-[180px] w-44 h-32 bg-white/5 rounded-2xl px-4 py-4 mr-2 last:mr-0 animate-pulse"
                >
                  <div className="w-24 h-6 bg-white/10 rounded-full mb-4"></div>
                  <div className="w-20 h-8 bg-white/10 rounded-full"></div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {interestSliderItems.length > 0 ? (
              <HorizontalCardSlider items={interestSliderItems} />
            ) : (
              <div className="text-white/60 text-xs font-roboto">{t('search.no_interests_found')}</div>
            )}
          </>
        )}
      </section>
    </div>
  );
}; 